import * as React from "react";
import { useState } from "react";
import type { CallLogDto } from "../../../services/telephonyService";
import type { MailLinkDto } from "../../../services/mailLinkService";
import { SenderAvatar } from "../../mail/SenderAvatar";
import { graphMailService, type Email } from "../../../services/graphMailService";
import { InitialsAvatar } from "../../chat/chatUi";
import {
    DIRECTION_LABEL,
    displayNameOf,
    isMissedInbound,
    statusLabel,
} from "../../telephony/telephonyUtils";
import { IcoAnruf, IcoChevron } from "./vg3Icons";
import { Vg3Kontextmenue, useKontextmenue, type MenuePunkt } from "./Vg3Kontextmenue";
import { datumKurz, type VerlaufEintrag } from "./verlauf";
import { verlaufDomId } from "./kommentarBezug";
import "./vorgangV3.css";

/**
 * Die Eintragsarten der Zeitachse: Mail und Anruf.
 *
 * Seit PO 08.09.2026 zeigt die Achse nur noch, was von außen kam. Notizen und
 * Kommentare sind in die Blockfläche darüber gewandert — der Verlauf ist
 * wieder das Ereignisprotokoll, nicht die Schreibfläche.
 *
 * Einheitlicher Zeilenschluss (PO 03.09.2026): rechts außen steht IMMER der
 * Zeitpunkt. Seltenes — „Zuordnung lösen" — liegt im Rechtsklick-Menü statt
 * als Knopf in der Zeile.
 *
 * Mails erscheinen als Outlook-artige Zeile: Chevron (nur mit Konversation),
 * Foto/Initialen, Betreff, darunter Von→An und der Textanriss.
 */

const fmtDauer = (sek?: number | null): string => {
    if (!sek || sek <= 0) return "";
    const m = Math.floor(sek / 60);
    const s = sek % 60;
    return m ? `${m}:${String(s).padStart(2, "0")} Min.` : `${s} Sek.`;
};

/** Zeitpunkt rechts außen — in jeder Eintragsart dieselbe Stelle. */
const Wann: React.FC<{ at: Date }> = ({ at }) => (
    <span className="vg3-wann">{datumKurz(at)}</span>
);

/* ─────────────────────────── Mail ─────────────────────────── */

type ThreadState = { laden: boolean; mails: Email[]; fehler: string | null };

export const MailEintrag: React.FC<{
    link: MailLinkDto;
    at: Date;
    onOeffnen: () => void;
    onOeffneThreadMail: (mailbox: string, email: Email) => void;
    onLoesen?: () => void;
    /** Weitere Menüpunkte (Zuweisung an ein To-do) — vom Aufrufer gestellt. */
    zusatzPunkte?: MenuePunkt[];
}> = ({ link, at, onOeffnen, onOeffneThreadMail, onLoesen, zusatzPunkte = [] }) => {
    const von = link.senderName || link.senderEmail || "Unbekannt";
    const [thread, setThread] = useState<ThreadState | null>(null);
    const menue = useKontextmenue();
    // Ohne Konversations-Id gibt es nichts nachzuladen (Alt-Links, Einzelmails).
    const hatThread = !!link.conversationId;

    /**
     * Den übrigen Verlauf der Konversation nachladen — rein zum Lesen. Verknüpft
     * bleibt allein die zugewiesene Nachricht; sie fällt deshalb aus der Liste.
     */
    const threadUmschalten = async () => {
        if (thread) {
            setThread(null);
            return;
        }
        setThread({ laden: true, mails: [], fehler: null });
        try {
            const mails = await graphMailService.fetchConversation(link.mailbox, link.conversationId ?? "");
            setThread({ laden: false, mails: mails.filter((m) => m.id !== link.messageId), fehler: null });
        } catch (err: any) {
            setThread({ laden: false, mails: [], fehler: err?.message ?? "Verlauf konnte nicht geladen werden." });
        }
    };

    const punkte: MenuePunkt[] = [
        { label: "Mail öffnen", onClick: onOeffnen },
        ...(hatThread
            ? [{ label: thread ? "Verlauf zuklappen" : "Verlauf anzeigen", onClick: () => void threadUmschalten() }]
            : []),
        ...zusatzPunkte,
        ...(onLoesen
            ? [{ label: "Zuordnung lösen", gefahr: true, trennerDavor: true, onClick: onLoesen }]
            : []),
    ];

    return (
        <div id={verlaufDomId(`mail-${link.id}`)} className="vg3-eintrag mail" onContextMenu={menue.oeffnen}>
            {/* Die Karte ist ein `div` mit Knopf-Rolle, kein <button>: der
                Chevron sitzt INNERHALB (PO 03.09.2026) und muss ein eigener,
                tastaturbedienbarer Knopf bleiben — verschachtelte Buttons sind
                kein gültiges HTML. */}
            <div
                role="button"
                tabIndex={0}
                className="vg3-mailkarte"
                onClick={onOeffnen}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onOeffnen();
                    }
                }}
            >
                {/* Ohne Konversation kein Platzhalter — der Chevron erscheint
                    nur, wo es wirklich etwas aufzuklappen gibt. */}
                {hatThread && (
                    <button
                        type="button"
                        className={`vg3-mailchev${thread ? " open" : ""}`}
                        aria-expanded={!!thread}
                        title={thread ? "Verlauf zuklappen" : "Verlauf der Konversation anzeigen"}
                        aria-label={thread ? "Verlauf zuklappen" : "Verlauf aufklappen"}
                        onClick={(e) => {
                            e.stopPropagation();
                            void threadUmschalten();
                        }}
                    >
                        <IcoChevron size={14} />
                    </button>
                )}

                <SenderAvatar name={von} email={link.senderEmail} size={38} />

                <span className="vg3-mailkarte-b">
                    <span className="vg3-mail-betreff">{link.subject || "(Kein Betreff)"}</span>
                    <span className="vg3-mail-an">
                        {von}
                        {link.recipients ? ` → ${link.recipients}` : ""}
                    </span>
                    {link.preview && <span className="vg3-mail-vorschau">{link.preview}</span>}
                </span>

                <span className="vg3-zeilenende">
                    {link.source === "auto-reply" && (
                        <span
                            className="vg3-badge auto"
                            title="Automatisch verknüpft — Antwort in einer bereits zugeordneten Konversation"
                        >
                            Auto
                        </span>
                    )}
                    <Wann at={at} />
                </span>
            </div>

            {thread && (
                <div className="vg3-thread">
                    {thread.laden ? (
                        <div className="vg3-thread-hint">Verlauf wird geladen …</div>
                    ) : thread.fehler ? (
                        <div className="vg3-thread-hint">{thread.fehler}</div>
                    ) : thread.mails.length === 0 ? (
                        <div className="vg3-thread-hint">Keine weiteren Nachrichten in diesem Verlauf.</div>
                    ) : (
                        thread.mails.map((m) => (
                            <button
                                key={m.id}
                                type="button"
                                className="vg3-threadzeile"
                                onClick={() => onOeffneThreadMail(link.mailbox, m)}
                            >
                                <SenderAvatar name={m.senderName || m.senderEmail || "?"} email={m.senderEmail} size={26} />
                                <span className="vg3-threadzeile-b">
                                    <span className="vg3-threadzeile-t">{m.subject || "(Kein Betreff)"}</span>
                                    <span className="vg3-threadzeile-s">
                                        {m.senderName || m.senderEmail}
                                        {m.preview ? ` — ${m.preview}` : ""}
                                    </span>
                                </span>
                                <Wann at={m.date} />
                            </button>
                        ))
                    )}
                </div>
            )}

            {menue.pos && (
                <Vg3Kontextmenue x={menue.pos.x} y={menue.pos.y} punkte={punkte} onClose={menue.schliessen} />
            )}
        </div>
    );
};

/* ────────────────────────── Anruf ─────────────────────────── */

export const AnrufEintrag: React.FC<{
    call: CallLogDto;
    at: Date;
    zusatzPunkte?: MenuePunkt[];
    /**
     * Schlüssel des Eintrags (`anruf-<id>-<todo|v>`): Ein Anruf kann seit dem
     * 01.10.2026 mehrfach im Verlauf stehen — die DOM-Id muss je Eintrag
     * eindeutig sein, nicht je Anruf.
     */
    eintragKey?: string;
}> = ({ call, at, zusatzPunkte = [], eintragKey }) => {
    const verpasst = isMissedInbound(call) && !call.resolvedAt;
    const dauer = fmtDauer(call.talkDurationSec ?? call.callDurationSec);
    const menue = useKontextmenue();
    const punkte: MenuePunkt[] = [...zusatzPunkte];
    return (
        <div id={verlaufDomId(eintragKey ?? `anruf-${call.id}`)} className="vg3-eintrag" onContextMenu={punkte.length ? menue.oeffnen : undefined}>
            <span className="vg3-marke anruf">
                <IcoAnruf />
            </span>
            <div className="vg3-body">
                <div className="vg3-zeile1">
                    <span className="vg3-wer" style={{ color: verpasst ? "var(--danger)" : undefined }}>
                        {displayNameOf(call)}
                    </span>
                    {verpasst && <span className="vg3-badge verpasst">Verpasst</span>}
                    <span className="vg3-zeilenende">
                        <Wann at={at} />
                    </span>
                </div>
                <div className="vg3-sub">
                    {[DIRECTION_LABEL[call.direction], statusLabel(call.status), dauer].filter(Boolean).join(" · ")}
                </div>
                {call.notes && <div className="vg3-text">{call.notes}</div>}
            </div>

            {menue.pos && (
                <Vg3Kontextmenue x={menue.pos.x} y={menue.pos.y} punkte={punkte} onClose={menue.schliessen} />
            )}
        </div>
    );
};

/* ───────────────────── Verteiler je Eintrag ───────────────── */

export const EintragZeile: React.FC<{
    eintrag: VerlaufEintrag;
    canEdit: boolean;
    onOeffneMail: (link: MailLinkDto) => void;
    onOeffneThreadMail: (mailbox: string, email: Email) => void;
    onLoeseMail: (linkId: number) => void;
    onNeuLaden: () => void;
    /** Zusätzliche Rechtsklick-Punkte für DIESEN Eintrag (To-do-Zuweisung). */
    zusatzPunkte?: MenuePunkt[];
    /** Teil der Mehrfachauswahl — die Zeile wird sichtbar hervorgehoben. */
    ausgewaehlt?: boolean;
}> = ({
    eintrag,
    canEdit,
    onOeffneMail,
    onOeffneThreadMail,
    onLoeseMail,
    zusatzPunkte = [],
    ausgewaehlt = false,
}) => {
    /** Hülle mit der Auswahl-Markierung — für beide Arten dieselbe. */
    const huelle = (inhalt: React.ReactNode) =>
        ausgewaehlt ? <div className="vg3-gewaehlt">{inhalt}</div> : <>{inhalt}</>;

    if (eintrag.art === "mail" && eintrag.mail) {
        const link = eintrag.mail;
        return huelle(
            <MailEintrag
                link={link}
                at={eintrag.at}
                onOeffnen={() => onOeffneMail(link)}
                onOeffneThreadMail={onOeffneThreadMail}
                onLoesen={canEdit ? () => onLoeseMail(link.id) : undefined}
                zusatzPunkte={zusatzPunkte}
            />,
        );
    }
    if (eintrag.art === "anruf" && eintrag.anruf) {
        return huelle(
            <AnrufEintrag call={eintrag.anruf} at={eintrag.at} zusatzPunkte={zusatzPunkte} eintragKey={eintrag.key} />,
        );
    }
    // Dokumente stehen seit PO 04.09.2026 NICHT mehr in der Zeitachse — sie
    // leben in der Anhang-Leiste darüber, samt ihrer Aktionen.
    // `VerlaufAbschnitt` filtert sie über `achsenEintraege` schon vorher
    // heraus; hier bleibt nur das defensive `null`.
    return null;
};

/** Avatar-Kreis eines Users — Wiederverwendung im Beteiligten-Feld. */
export const UserKreis: React.FC<{ name: string; initials: string; userId?: string | null; size?: number }> = ({
    name,
    initials,
    userId,
    size = 26,
}) => <InitialsAvatar name={name} label={initials} size={size} userId={userId} />;
